import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { listBroadcasts } from "@/lib/emailApi";
import type { ReportSummary, SystemStatus } from "@/lib/types";
import { formatInt, formatPercent, formatWhen } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { ReleaseLabel } from "@/components/ReleaseLabel";
import { WorkerBadges } from "@/components/WorkerBadges";

/** What needs a look today: duplicates waiting, broadcasts on the schedule, the latest sends, system health. */
export function HomePage() {
  const status = useQuery({ queryKey: ["system-status"], queryFn: () => api<SystemStatus>("/api/v1/system/status"), refetchInterval: 30_000 });
  const dups = useQuery({ queryKey: ["match-reviews-count"], queryFn: () => api<{ pending: number }>("/api/v1/match-reviews/count").then((r) => r.pending) });
  const scheduled = useQuery({ queryKey: ["broadcasts", "", "scheduled"], queryFn: () => listBroadcasts({ status: "scheduled" }) });
  const latest = useQuery({ queryKey: ["email-reports-home"], queryFn: () => api<{ reports: ReportSummary[] }>("/api/v1/email-reports?limit=6") });
  const undelivered = useQuery({ queryKey: ["delivery-problems-home"], queryFn: () => api<{ orders: unknown[] }>("/api/v1/billing/delivery-problems").then((r) => r.orders.length) });
  const s = status.data;
  const problems = s && (s.database !== "up" || s.valkey !== "up" || s.spool.valkey_pending + s.spool.spaces_pending > 0);
  return (
    <div className="mx-auto max-w-6xl space-y-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold">Home</h1>
        <span className="text-sm text-[var(--color-muted-foreground)]">Press ⌘K (Ctrl-K on Windows) to find anyone, or to jump to a page.</span>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Possible duplicates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{dups.data !== undefined ? formatInt(dups.data) : "…"}</div>
            <Link to="/contacts/duplicates" className="text-sm text-[var(--color-primary)] hover:underline">
              {dups.data ? "Review them" : "Nothing waiting"}
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Paid, something missing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{undelivered.data !== undefined ? formatInt(undelivered.data) : "…"}</div>
            <Link to="/billing/delivery-problems" className="text-sm text-[var(--color-primary)] hover:underline">
              {undelivered.data ? "See what's missing" : "Every paid order is delivered"}
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Scheduled broadcasts</CardTitle>
            {scheduled.data?.sample && <Badge variant="warning">sample</Badge>}
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {(scheduled.data?.data ?? []).length === 0 && <p className="text-[var(--color-muted-foreground)]">None on the schedule.</p>}
            {(scheduled.data?.data ?? []).slice(0, 4).map((b) => (
              <div key={b.id} className="flex items-center gap-2">
                <BrandMark id={b.brand_id} />
                <Link to="/email/broadcasts/$broadcastId" params={{ broadcastId: String(b.id) }} className="min-w-0 flex-1 truncate text-[var(--color-primary)] hover:underline">
                  {b.name}
                </Link>
                <span className="whitespace-nowrap text-xs text-[var(--color-muted-foreground)]">{formatWhen(b.scheduled_at)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>System</CardTitle>
            {s && <ReleaseLabel release={s.release} version={s.version} />}
          </CardHeader>
          <CardContent className="flex flex-wrap gap-1.5 text-sm">
            {!s && <span className="text-[var(--color-muted-foreground)]">Checking…</span>}
            {s && !problems && <Badge variant="success">Everything is up</Badge>}
            {s && problems && (
              <>
                <Badge variant={s.database === "up" ? "success" : "destructive"}>Database {s.database}</Badge>
                <Badge variant={s.valkey === "up" ? "success" : "destructive"}>Valkey {s.valkey}</Badge>
                <Badge variant="warning">Outage spool: {s.spool.valkey_pending + s.spool.spaces_pending} waiting</Badge>
              </>
            )}
            {s && <WorkerBadges workers={s.workers} okVariant="muted" />}
            <Link to="/settings" search={{ tab: "health" }} className="w-full text-xs text-[var(--color-primary)] hover:underline">
              Details
            </Link>
          </CardContent>
        </Card>
      </div>
      <Card className="overflow-x-auto">
        <CardHeader>
          <CardTitle>Latest sends</CardTitle>
          <Link to="/email/reports" className="text-xs text-[var(--color-primary)] hover:underline">
            All reports
          </Link>
        </CardHeader>
        {(latest.data?.reports ?? []).length === 0 ? (
          <p className="px-4 py-3 text-sm text-[var(--color-muted-foreground)]">No sends yet.</p>
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Sent (MT)</TH>
                <TH>Email</TH>
                <TH>Brand</TH>
                <TH className="text-right">Sent</TH>
                <TH className="text-right">Opened (people)</TH>
                <TH className="text-right">Clicked</TH>
              </tr>
            </THead>
            <tbody>
              {latest.data!.reports.map((r) => (
                <TR key={r.report_unit_id}>
                  <TD className="num whitespace-nowrap">{formatWhen(r.first_sent_at)}</TD>
                  <TD className="max-w-[24rem]">
                    <Link to="/email/reports/$reportId" params={{ reportId: String(r.report_unit_id) }} className="block truncate text-[var(--color-primary)] hover:underline">
                      {r.name}
                    </Link>
                  </TD>
                  <TD>{r.brand_id ? <BrandMark id={r.brand_id} /> : "—"}</TD>
                  <TD className="num text-right">{formatInt(r.sent)}</TD>
                  <TD className="num text-right">{formatPercent(r.human_open_rate)}</TD>
                  <TD className="num text-right">{formatPercent(r.click_rate)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
